import moment from 'moment-timezone';
import styles from './TeamsTimeline.module.scss';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import { IoRefresh } from 'react-icons/io5';
const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || '—';
};
const formatSecondsToHHMM = (sec = 0) => {
  const totalMins = Math.floor(Number(sec) / 60);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const formatTimeInTz = (date, timeZone) => {
  if (!date) return '—';
  return moment(date).tz(timeZone).format('hh:mm A');
};

const ACTIVE_WINDOW_MS = 6 * 60 * 1000;

const toMillisOrNull = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') {
    return value.toDate().getTime();
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  const parsed = moment(value);
  if (!parsed.isValid()) return null;
  return parsed.valueOf();
};

const isActiveByLastActivity = (lastActivityAt, nowMs) => {
  const lastActivityMs = toMillisOrNull(lastActivityAt);
  if (lastActivityMs == null) return false;
  return nowMs - lastActivityMs <= ACTIVE_WINDOW_MS;
};

export const teamsTimelineColumnConfig = ({
  nowMs = Date.now(),
  onRefreshStatus,
  isRefreshingStatus = false,
} = {}) => {
  return [
    {
      id: 'name',
      name: 'Name',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <div className={styles.nameCell}>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center mr-2 text-white font-weight-bold"
              style={{
                width: 36,
                height: 36,
                minWidth: 36,
                backgroundColor:
                  '#' +
                  Math.floor(
                    (row.userUID || '')
                      .split('')
                      .reduce(
                        (a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0,
                        0
                      ) & 0xffffff
                  )
                    .toString(16)
                    .padStart(6, '0'),
              }}
            >
              {getInitials(row.userName)}
            </div>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Medium}
            >
              {row.userName || '—'}
            </Text>
          </div>
        );
      },
    },
    {
      id: 'timeZone',
      name: 'Time Zone',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Medium}
          >
            {row.timeZone || '—'}
          </Text>
        );
      },
    },
    {
      id: 'checkIn',
      name: 'Check-in',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Medium}
          >
            {row.checkIn ? formatTimeInTz(row.checkIn, row.timeZone) : '—'}
          </Text>
        );
      },
    },
    {
      id: 'checkOut',
      name: 'Check-out',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Medium}
          >
            {row.checkOut ? formatTimeInTz(row.checkOut, row.timeZone) : '—'}
          </Text>
        );
      },
    },
    {
      id: 'status',
      name: (
        <div className={styles.statusHeaderCell}>
          <span>Status</span>
          <button
            type="button"
            className={styles.statusRefreshButton}
            onClick={onRefreshStatus}
            title={
              isRefreshingStatus ? 'Refreshing status...' : 'Refresh status'
            }
            aria-label={
              isRefreshingStatus ? 'Refreshing status...' : 'Refresh status'
            }
            disabled={isRefreshingStatus}
          >
            <IoRefresh
              className={
                isRefreshingStatus ? styles.statusRefreshIconSpinning : ''
              }
            />
          </button>
        </div>
      ),
      sortable: false,
      cellRenderer: (row) => {
        const isActive = isActiveByLastActivity(row.lastActivityAt, nowMs);
        return (
          <div className={styles.statusCell}>
            <span
              className={`${styles.statusDot} ${
                isActive ? styles.statusDotActive : styles.statusDotInactive
              }`}
            />
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Medium}
            >
              {isActive ? 'Active' : 'Inactive'}
            </Text>
          </div>
        );
      },
    },
    {
      id: 'workTime',
      name: 'Work Time',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Medium}
          >
            {formatSecondsToHHMM(row.workSec)}
          </Text>
        );
      },
    },
    {
      id: 'productiveTime',
      name: 'Productive Time',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Medium}
          >
            {formatSecondsToHHMM(row.productiveSec)}
          </Text>
        );
      },
    },
    {
      id: 'idleTime',
      name: 'Idle Time',
      sortable: false,
      cellRenderer: (row) => {
        const { displayIdle } = normalizeIdleForDisplay(
          row.workSec,
          row.idleSec
        );
        return (
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Medium}
          >
            {formatSecondsToHHMM(displayIdle)}
          </Text>
        );
      },
    },
  ];
};
